import { useCallback, useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

export type ManageListFilter = 'attention_needed' | 'all';

function readFilterFromParams(p: URLSearchParams): ManageListFilter {
  const v = p.get('attention_needed');
  // Explicit "all"; otherwise default to Attention needed (incl. absent / true).
  if (v === 'false' || v === '0') return 'all';
  return 'attention_needed';
}

/**
 * Keeps accounts/campaigns toolbar filter + search in sync with the URL
 * (`attention_needed`, `search`) for deep links and refresh/share.
 * Default filter is Attention needed; `attention_needed=false` selects All.
 */
export function useManageListUrlParams() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filter, setFilter] = useState<ManageListFilter>(() => readFilterFromParams(searchParams));
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('search') || '');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setFilter(readFilterFromParams(params));
    setSearchQuery(params.get('search') || '');
  }, [location.search]);

  const setFilterWithUrl = useCallback(
    (next: ManageListFilter) => {
      setFilter(next);
      const nextParams = new URLSearchParams(searchParams);
      if (next === 'all') {
        nextParams.set('attention_needed', 'false');
      } else {
        // Default filter — keep URL clean (absent) or explicit true for shares.
        nextParams.delete('attention_needed');
      }
      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const setSearchWithUrl = useCallback(
    (value: string) => {
      setSearchQuery(value);
      const nextParams = new URLSearchParams(searchParams);
      const t = value.trim();
      if (t) {
        nextParams.set('search', t);
      } else {
        nextParams.delete('search');
      }
      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  return {
    filter,
    searchQuery,
    setFilterWithUrl,
    setSearchWithUrl,
  };
}
